import dayjs from 'dayjs';
import { useEffect, useMemo, useReducer } from 'react';

import { formatGraphQLDate, formatInputDate } from '../../utils/date';
import { Input } from '../input/Input';
import { SimpleSelect } from '../select/SimpleSelect';
import { IDateRange, TimeFrameAction, TimeFrameState, TimeFrameType } from './types';

function calculateDateRange(type: TimeFrameType): IDateRange {
  switch (type) {
    case TimeFrameType.Today: {
      const date = dayjs();
      return {
        startDate: formatGraphQLDate(date),
        endDate: formatGraphQLDate(date),
      };
    }
    case TimeFrameType.Week: {
      const monday = dayjs().subtract(1, 'day').set('day', 1);
      const sunday = monday.add(6, 'day');
      return {
        startDate: formatGraphQLDate(monday),
        endDate: formatGraphQLDate(sunday),
      };
    }
    case TimeFrameType.Month: {
      const firstDayOfMonth = dayjs().set('date', 1);
      const lastDayOfMonth = firstDayOfMonth.add(1, 'month').subtract(1, 'day');
      return {
        startDate: formatGraphQLDate(firstDayOfMonth),
        endDate: formatGraphQLDate(lastDayOfMonth),
      };
    }
    case TimeFrameType.Year: {
      const firstDayOfYear = dayjs().set('date', 1).set('month', 0);
      const lastDayOfYear = firstDayOfYear.add(1, 'year').subtract(1, 'day');
      return {
        startDate: formatGraphQLDate(firstDayOfYear),
        endDate: formatGraphQLDate(lastDayOfYear),
      };
    }
    default: {
      return {
        startDate: formatGraphQLDate(dayjs()),
        endDate: formatGraphQLDate(dayjs()),
      };
    }
  }
}

function timeFrameReducer(state: TimeFrameState, action: TimeFrameAction): TimeFrameState {
  switch (action.action) {
    case 'select:type': {
      let newState: TimeFrameState = { ...state, type: action.newType };
      if (newState.type !== TimeFrameType.Custom) {
        const range = calculateDateRange(action.newType);
        newState = { ...newState, ...range };
      }
      return newState;
    }
    case 'set:start-date': {
      return {
        ...state,
        type: TimeFrameType.Custom,
        startDate: formatGraphQLDate(dayjs(action.value)),
      };
    }
    case 'set:end-date': {
      return {
        ...state,
        type: TimeFrameType.Custom,
        endDate: formatGraphQLDate(dayjs(action.value)),
      };
    }
    default:
      return state;
  }
}

export interface TimeFrameProps {
  defaultType?: TimeFrameType;
  onChange: (val: TimeFrameState) => void;
}

const ITEMS = [
  {
    key: TimeFrameType.Today,
    name: 'Vandaag',
  },
  {
    key: TimeFrameType.Week,
    name: 'Deze week',
  },
  {
    key: TimeFrameType.Month,
    name: 'Deze maand',
  },
  {
    key: TimeFrameType.Year,
    name: 'Dit jaar',
  },
  {
    key: TimeFrameType.Custom,
    name: 'Kies een periode',
  },
];

export const TimeFrame: React.FC<TimeFrameProps> = (props) => {
  const { defaultType, onChange } = props;
  const initialState = useMemo(() => {
    const initialType = defaultType || TimeFrameType.Today;
    const initialDateRange = calculateDateRange(initialType);
    const initialState: TimeFrameState = {
      type: initialType,
      startDate: initialDateRange.startDate,
      endDate: initialDateRange.endDate,
    };
    return initialState;
  }, [defaultType]);
  const [state, dispatch] = useReducer(timeFrameReducer, initialState);

  useEffect(() => {
    onChange(state);
  }, [state]);

  const items = [...ITEMS];
  return (
    <div className="flex flex-wrap items-end gap-3">
      <label className="min-w-0">
        <div className="label-text mb-1.5 w-full">Van</div>
        <Input
          type="date"
          value={formatInputDate(state.startDate)}
          onChange={(value) => {
            dispatch({
              action: 'set:start-date',
              value,
            });
          }}
        />
      </label>

      <label className="min-w-0">
        <div className="label-text mb-1.5 w-full">Tot</div>
        <Input
          type="date"
          value={formatInputDate(state.endDate)}
          onChange={(value) => {
            dispatch({
              action: 'set:end-date',
              value,
            });
          }}
        />
      </label>

      <div className="w-56 min-w-0">
        <div className="label-text mb-1.5 w-full">Periode</div>
        <SimpleSelect
          items={items}
          selectedItem={items.find((v) => v.key === state.type) ?? null}
          onSelect={(v) => {
            if (v) {
              dispatch({
                action: 'select:type',
                newType: v.key as TimeFrameType,
              });
            }
          }}
        />
      </div>
    </div>
  );
};
